import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";

/* PM-account password auth (2026-02) — the landing page for the email link sent by
 * ForgotPassword.jsx (POST /auth/forgot-password). Does NOT auto-login on success (matches the
 * backend's own /auth/reset-password contract) — the account holder signs in fresh with their
 * new password, same as any other password change. */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
    } catch (ex) {
      // Staff password reset fix (2026-10, length minimum lowered back to 8 in a later pass):
      // /auth/reset-password returns a PLAIN STRING detail for an invalid/expired token, but a
      // DICT ({code, message}) for a weak_password rejection (e.g. a common password, or one
      // failing the complexity check) — this page only ever compared `detail` against the
      // string case, so any dict-shaped error fell through to the generic message below with
      // no way to tell what was actually wrong. Mirrors StaffSetPassword.jsx's handling.
      const detail = ex?.response?.data?.detail;
      const code = typeof detail === "object" ? detail.code : detail;
      const message = typeof detail === "object" ? detail.message : null;
      setErr(code === "invalid_or_expired_token" ? "This reset link is invalid or has expired — request a new one." : (message || "Couldn't reset your password — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-canvas px-4" data-testid="reset-password-page">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex flex-col items-start gap-4">
          <Logo theme="light" size="md" testId="reset-password-logo" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-faint">Account recovery</div>
            <h2 className="font-editorial text-2xl text-ink mt-1">Choose a new password</h2>
          </div>
        </div>

        {!token ? (
          <div data-testid="reset-password-no-token" className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-4">
            This link is missing its reset token — please use the link from your email exactly as sent.
          </div>
        ) : done ? (
          <div data-testid="reset-password-success" className="text-sm text-ink-soft leading-relaxed bg-white border border-hairline rounded-md px-4 py-4 space-y-3">
            <p>Your password has been reset.</p>
            <a href="/login" data-testid="reset-password-go-to-login" className="inline-block rounded-md bg-brand px-4 py-2 text-sm font-semibold text-ink hover:bg-brand-dim transition-colors">Sign in →</a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">New password</label>
              <input
                data-testid="reset-password-new-input"
                type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                minLength={8} autoFocus
                className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Confirm new password</label>
              <input
                data-testid="reset-password-confirm-input"
                type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
              />
            </div>
            {err && <div data-testid="reset-password-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
            <button
              data-testid="reset-password-submit"
              disabled={busy}
              className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
            >{busy ? "Saving…" : "Reset password"}</button>
          </form>
        )}

        <div className="mt-7 pt-6 border-t border-hairline">
          <a href="/login" data-testid="reset-password-back-to-login" className="text-[11px] text-faint hover:text-ink transition-colors">← Back to sign in</a>
        </div>
      </div>
    </div>
  );
}
